export interface TroubleshootingItem {
  id: string
  icon: string // lucide-react icon name hint for the component
  problem: string // What the user sees — written as a user would describe it
  cause: string // Why it happens — plain language, no jargon
  fix: string // Step-by-step resolution — markdown supported
  tags: string[] // Search keywords for WidgetHome filtering
}

export const troubleshootingItems: TroubleshootingItem[] = [
  {
    id: 'connection-loss',
    icon: 'wifi-off',
    problem: "My app shows 'Connection Lost' or goes blank",
    cause:
      'The platform sometimes updates in the background, which briefly disconnects your session. This usually takes 3–5 minutes.',
    fix: "Wait a few minutes and refresh the page. If your app still isn't loading after 10 minutes, check its status on the app detail page. If the problem continues, let us know in **#team-claude** on Slack.",
    tags: ['connection', 'lost', 'blank', 'disconnected', 'offline', 'update'],
  },
  {
    id: 'preview-wrong-content',
    icon: 'eye',
    problem: "My preview doesn't show my latest changes",
    cause:
      'Code edits update the dev preview automatically, but the published version needs to be rebuilt manually by clicking Publish.',
    fix: "Try a hard refresh (**Ctrl+Shift+R**). If the **published** version didn't update, click **Publish** again — code edits only update the dev preview automatically. Check which URL you're looking at: `/dev/` is the dev preview, the main URL is the published version.",
    tags: ['preview', 'changes', 'not showing', 'old version', 'dev', 'production', 'url'],
  },
  {
    id: 'terminal-paste',
    icon: 'terminal',
    problem: "I can't paste text into the terminal",
    cause:
      'Your browser intercepts the paste shortcut before it reaches the terminal. This is a known browser limitation.',
    fix: 'Right-click inside the terminal area and select **Paste** from the context menu. On Mac, **Cmd+V** usually works directly.',
    tags: ['paste', 'terminal', 'ctrl+v', 'clipboard', 'copy'],
  },
  {
    id: 'app-not-starting',
    icon: 'box',
    problem: "My app won't start or shows an error",
    cause:
      'Something in your app is preventing it from starting — usually a missing file, a configuration issue, or a code error.',
    fix: "Check the error message on the app detail page. The easiest fix: open the terminal and ask Claude Code — *\"My app won't start, can you check the logs and fix it?\"* — it will diagnose the issue and apply a fix automatically.",
    tags: ['start', 'error', 'crash', 'boot', 'manifest', 'dependency', 'broken', 'port', '502'],
  },
  {
    id: 'app-502',
    icon: 'alert-circle',
    problem: "My app shows a 502 Bad Gateway error",
    cause:
      "Your app isn't responding. It may have crashed, stopped running, or has a configuration issue.",
    fix: "Check your app's status on the detail page. If it's **stopped**, start it. If it shows **error**, open the terminal and ask Claude Code: *\"My app is showing 502, can you fix it?\"* — it will check the logs and resolve the issue.",
    tags: ['502', 'bad gateway', 'not working', 'unreachable', 'proxy', 'port'],
  },
  {
    id: 'app-slow-loading',
    icon: 'box',
    problem: 'My app takes a long time to load',
    cause:
      "Apps that haven't been visited in a while are stopped to save resources. When someone visits again, the app needs to be started, which takes a moment.",
    fix: "A stopped app automatically restarts when someone visits it, which takes 15–60 seconds. Once running, load times are fast. To keep your app always ready, enable **keep_alive** in app settings — this prevents it from being stopped due to inactivity.",
    tags: ['slow', 'loading', 'cold start', 'wait', 'performance', 'idle', 'keep alive'],
  },
  {
    id: 'changes-not-deploying',
    icon: 'eye',
    problem: 'I deployed my app but nothing changed',
    cause:
      'Your browser may be showing an older cached version. Or you edited code but didn\'t click Publish to update the live version.',
    fix: "Hard refresh: **Ctrl+Shift+R** (Windows/Linux) or **Cmd+Shift+R** (Mac). If that doesn't work, try opening in incognito. If you edited code, remember to click **Publish** — workspace edits only update the dev preview automatically.",
    tags: ['deploy', 'cache', 'old version', 'refresh', 'nothing changed', 'stale'],
  },
  {
    id: 'workspace-files-missing',
    icon: 'folder-open',
    problem: 'My files disappeared from the workspace',
    cause:
      'Your project files are saved permanently in the workspace. However, files saved in temporary locations are cleared when the workspace restarts.',
    fix: "Your main project files should still be there. Open the terminal and ask Claude Code: *\"My files seem to be missing, can you help me find them?\"* If you imported from GitHub, you can re-import to restore. If files are truly gone, report it in **#team-claude** on Slack.",
    tags: ['files', 'missing', 'disappeared', 'lost', 'workspace', 'volume'],
  },
  {
    id: 'permission-denied',
    icon: 'lock',
    problem: "I see 'Permission Denied' or 'Access Denied'",
    cause:
      "You don't have the right access level for this app. The owner controls who can view, edit, or manage each app.",
    fix: "Ask the app owner to share the app with you — they can do this from the app's share settings. If you need to edit code or start/stop the app, ask for **Editor** access. If you're the owner and seeing this unexpectedly, try logging out and back in.",
    tags: ['permission', 'denied', 'access', 'forbidden', 'share', 'owner', '403'],
  },
  {
    id: 'app-auto-stopped',
    icon: 'box',
    problem: 'My app stopped running on its own',
    cause:
      'Apps are automatically stopped after 6 hours without any visitors to save platform resources. Your code and data are completely safe.',
    fix: "Your code and data are NOT deleted — nothing is lost. The app will automatically restart the next time someone visits it. You can also start it manually from the app detail page. To prevent auto-stopping, enable **keep_alive** in app settings and your app will stay running all the time.",
    tags: ['stopped', 'auto', 'idle', 'keep alive', 'cleanup', 'timeout'],
  },
  {
    id: 'build-failed',
    icon: 'alert-circle',
    problem: 'My publish build failed',
    cause:
      'Something went wrong while building the published version of your app — usually a missing dependency or a code error.',
    fix: "Check the build logs on the app detail page for details. The easiest fix: open the terminal and ask Claude Code — *\"My publish build failed, can you check the logs and fix it?\"* — it will diagnose the problem, apply a fix, and rebuild. Your previous working version stays live while you fix it.",
    tags: ['build', 'failed', 'production', 'error', 'npm', 'install', 'compile', 'publish'],
  },
  {
    id: 'blank-page',
    icon: 'alert-circle',
    problem: 'The page is completely blank or shows a white screen',
    cause:
      'An error prevented the page from loading. This can happen after a platform update or due to your browser caching an old version.',
    fix: "Hard refresh (**Ctrl+Shift+R**). If that doesn't help, try incognito mode. If the page is still blank, the platform may be updating — wait a few minutes. If it persists, report it in **#team-claude** on Slack.",
    tags: ['blank', 'white screen', 'empty', 'not loading', 'javascript error', 'cache'],
  },
  {
    id: 'preview-default-page',
    icon: 'eye',
    problem: 'Preview shows a default/error page instead of my app',
    cause:
      'New apps start empty — the preview shows a placeholder until you add your code. If you already added code, your browser may be showing a cached old page.',
    fix: "If you just created the app: open the terminal and describe what you want to build — Claude Code will create it for you. If you already imported files but still see the old page: hard refresh (**Ctrl+Shift+R**), or tell Claude Code in the terminal: *\"I'm not seeing my app, still showing the old page\"* and it will fix it.",
    tags: ['preview', 'default', 'error page', 'old page', 'import', 'index.html', 'not showing', 'placeholder'],
  },
]
